import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { DeepPartial, FieldPath, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FaInfoCircle } from 'react-icons/fa';
import { array, InferType, object, string } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  BasePricePayload, IntervalType, isApiQueryError, PermissionKeys,
  PriceReadModel, ProductCategory, ProductReadModel, TenantErpIntegrationReadModel, UnitScheme,
  useGetAgreementsQuery, useGetPricesQuery, useGetProductsByIdChildrenQuery, useGetProductsTripletexQuery, usePutProductsByIdMutation,
} from '@client/api';

import { TripletexProductSelect } from '../../components';
import {
  Button, Card, CardActions, CardContent, CardHeader, CardTitle,
  CurrencyAdornment, CurrencyAutocomplete, CurrencyCode,
  DD, DT, FormCancelButton, FormField, FormSubmitButton, getCurrencySymbol, GridCol, GridRow,
  InfoTooltip, Input, isDefinedNumber, isDefinedString, isValidDefinedPriceInterval,
  Label, Select, Text, toCurrencyStringOrUndefined, toIntervalString, Typography,
  YupRemoveIndex,
} from '@client/toolkit';

import { ProductPriceFormData, productPriceSchema } from '../../components/product-form/product-price-item.form';
import { CancelRequestModal } from '../../../../components';
import { PermissionBasedActionButton } from '../../../../components/permission-based-button/permission-based-button';
import { useTenantSettingsDefaults, useTenants } from '../../../../hooks';

const PRODUCT_CATEGORY_OPTIONS: ProductCategory[] = ['Other', 'Leie', 'Felleskostnader'];

const UNIT_SCHEME_OPTIONS: UnitScheme[] = ['AreaMetric', 'Custom'];

type FormModel = {
  name: string; description?: string; category?: ProductCategory;
  unitScheme?: UnitScheme; unitLabel?: string; sourceIdentifier?: string;
  prices?: ProductPriceFormData[];
};

const createSchema = (isErpConfigured: boolean) => object().shape({
  name: string().required(), description: string(),
  category: string<ProductCategory>(), unitScheme: string<UnitScheme>(),
  unitLabel: string(), sourceIdentifier: isErpConfigured ? string().required() : string(),
  prices: array().of(productPriceSchema).min(1).max(1),
}, []);

type FormValues = FormModel;
type FormPath = FieldPath<FormModel>;

export interface ProductInformationCardProps {
  className?: string; product?: ProductReadModel;
  isFetching?: boolean; onCategoryChange?: (c: ProductCategory | undefined) => void;
}

export function ProductInformationCard({ className, product, isFetching, onCategoryChange }: ProductInformationCardProps) {
  const { t, ready } = useTranslation('products');
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');
  const { t: tValidation } = useTranslation('validation');
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { defaultCurrencyCode } = useTenantSettingsDefaults();
  const { selectedTenant } = useTenants();
  const isNew = (location.state as any)?.isNew === true;
  const [editing, setEditing] = useState(isNew);

  // Check if ERP is configured for the current tenant
  const isErpConfigured = selectedTenant && typeof selectedTenant === 'object' && 'erpIntegrations' in selectedTenant
    ? !!selectedTenant.erpIntegrations?.some((erp: TenantErpIntegrationReadModel) => erp.configured)
    : false;

  // Clear the isNew state so refreshing doesn't re-enter edit mode
  useEffect(() => {
    if (isNew) window.history.replaceState({}, '');
  }, [isNew]);
  const [cancelingRequest, setCancelingRequest] = useState(false);
  const [putProduct] = usePutProductsByIdMutation();

  const { data: pricesData, isFetching: isFetchingPrices } = useGetPricesQuery(
    { 'productId.Equal': id ?? '', 'pagination.Limit': 20, 'pagination.Offset': 0 },
    { skip: id == null },
  );
  const prices = useMemo(() => pricesData?.items ?? [], [pricesData?.items]);
  const firstPrice = prices[0];

  const defaultValues: DeepPartial<FormValues> = useMemo(() => ({
    name: product?.name ?? undefined,
    description: product?.description ?? undefined,
    category: product?.category ?? 'Other',
    unitScheme: product?.unit?.scheme,
    unitLabel: product?.unit?.label ?? undefined,
    sourceIdentifier: product?.sourceId ?? undefined,
    prices: prices.length > 0
      ? prices.slice(0, 1).map<DeepPartial<ProductPriceFormData>>((x) => ({
          priceId: x.id ?? undefined, description: x.description ?? undefined,
          unitAmount: (x.unitAmount?.amount as number) ?? undefined,
          unitCurrencyCode: (x.unitAmount?.currency?.code as string) ?? defaultCurrencyCode ?? undefined,
          intervalCount: x.recurring ? 1 : undefined, intervalType: x.recurring ? 'Month' : undefined,
          hasInterval: x.recurring ?? false,
        }))
      : [{ priceId: '', description: '', hasInterval: true, intervalOption: '' as any,
          intervalCount: 1, intervalType: 'Month' as any, unitAmount: '' as any,
          unitCurrencyCode: defaultCurrencyCode ?? '' }],
  }), [product, prices, defaultCurrencyCode]);

  const methods = useForm<FormValues>({ defaultValues, mode: 'onTouched', resolver: yupResolver(createSchema(isErpConfigured)) });
  const { handleSubmit, formState: { isDirty }, reset, watch, control, getFieldState } = methods;
  const { fields } = useFieldArray<FormValues, 'prices'>({ control, name: 'prices' });
  const unitScheme = watch('unitScheme');
  const currentCategory = watch('category');
  const hasInterval = watch('prices.0.hasInterval' as any) as boolean | undefined;
  const unitCurrencyCode = watch('prices.0.unitCurrencyCode' as any) as string | undefined;
  const unitCurrencySymbol = isDefinedString(unitCurrencyCode) ? getCurrencySymbol(unitCurrencyCode) : undefined;

  useEffect(() => { if (editing && onCategoryChange) onCategoryChange(currentCategory as ProductCategory | undefined); }, [currentCategory, editing, onCategoryChange]);
  useEffect(() => { if (!editing) reset(defaultValues); }, [defaultValues, editing, reset]);

  const isFelleskostnader = currentCategory === 'Felleskostnader';
  const savedIsFelleskostnader = product?.category === 'Felleskostnader';

  const { data: subProducts } = useGetProductsByIdChildrenQuery(
    { id: id ?? '' },
    { skip: id == null || !savedIsFelleskostnader },
  );
  const hasSubProducts = savedIsFelleskostnader && (subProducts?.length ?? 0) > 0;

  const { data: agreementsData } = useGetAgreementsQuery(
    { 'productId.Equal': id ?? '', 'pagination.Limit': 0, 'pagination.Offset': 0 },
    { skip: id == null },
  );
  const hasAgreements = (agreementsData?.totalCount ?? 0) > 0;

  const { data: tripletexProducts } = useGetProductsTripletexQuery(undefined, {
    skip: id == null,
  });

  const tripletexProductName = tripletexProducts?.find((p) => String(p.id) === product?.sourceId)?.name;

  // Felleskostnader always uses AreaMetric
  useEffect(() => {
    if (!editing || !isFelleskostnader) return;
    const sv = (methods as any).setValue;
    sv('unitScheme', 'AreaMetric', { shouldDirty: true });
    sv('unitLabel', undefined, { shouldDirty: false });
  }, [currentCategory, editing, methods, isFelleskostnader]);

  const handleSelectUnitScheme = (e: ChangeEvent<HTMLSelectElement>) => {
    if ((e.target.value as UnitScheme | undefined) !== 'Custom')
      (methods as any).setValue('unitLabel', undefined, { shouldDirty: true });
  };

  const handleSelectHasInterval = (newHasInterval: boolean) => {
    const sv = (methods as any).setValue;
    if (hasInterval === newHasInterval) return;
    sv('prices.0.hasInterval', newHasInterval, { shouldDirty: true });
    if (!newHasInterval) {
      sv('prices.0.intervalOption', undefined, { shouldDirty: false });
      sv('prices.0.intervalCount', undefined, { shouldDirty: false });
      sv('prices.0.intervalType', undefined, { shouldDirty: false });
    } else {
      sv('prices.0.intervalCount', 1, { shouldDirty: false });
      sv('prices.0.intervalType', 'Month', { shouldDirty: false });
    }
  };

  const handleOpenEdit = () => setEditing(true);
  const checkCancelRequest = () => { if (isDirty) setCancelingRequest(true); else { setEditing(false); onCategoryChange?.(product?.category); } };
  const handleConfirmCancelRequest = () => { setEditing(false); setCancelingRequest(false); onCategoryChange?.(product?.category); };
  const handleCancelCancelRequest = () => setCancelingRequest(false);

  const onSubmit = async (data: FormValues) => {
    if (!product) return;
    const setPrices = (data.prices || []).map((x: ProductPriceFormData, idx: number) => ({
      unitAmount: { amount: x.unitAmount, currency: { code: x.unitCurrencyCode } },
      description: x.description,
      interval: x.hasInterval ? { count: x.intervalCount as number, type: x.intervalType as IntervalType } : undefined,
      id: prices[idx]?.id ?? undefined,
    }));
    try {
      await putProduct({ id: product.id, body: {
        name: data.name, description: data.description,
        category: data.category as ProductCategory,
        unit: isDefinedString(data.unitScheme) ? { scheme: data.unitScheme as UnitScheme, label: data.unitLabel } : undefined,
        sourceId: data.sourceIdentifier, prices: setPrices as any,
      } }).unwrap();
    } catch (e: unknown) {
      if (isApiQueryError(e) && e.status === 400) toast.error(tValidation(`server.${e.data.detail}`), { id: 'global-error' });
      return;
    }
    setEditing(false);
  };

  const pricesErrorMessage = getFieldState('prices').error?.message ? t('productForm.pricesErrorMessage') : undefined;

  return (
    <>
      <Card className={className}>
        <FormProvider {...methods}>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <CardHeader>
              <CardTitle className="flex-1 !text-[#f5bc6f]" variant="dense" loading={!ready}>{t('product.productInformation')}</CardTitle>
              <CardActions>
                {!editing ? (
                  <PermissionBasedActionButton permissions={[PermissionKeys.Tenant.ProductUpdate]} onClick={handleOpenEdit} disabled={isFetching || !product || hasAgreements} disabledTooltip={hasAgreements ? t('product.hasAgreementsEditDisabled') : undefined} title={tCommon('edit')} />
                ) : (
                  <>
                    <FormCancelButton size="sm" onClick={checkCancelRequest} color="primary-dark">{tCommon('cancel')}</FormCancelButton>
                    <FormSubmitButton type="submit" size="sm" disabled={isFetching} color="primary">{tCommon('save')}</FormSubmitButton>
                  </>
                )}
              </CardActions>
            </CardHeader>
            <CardContent>
              {!editing ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-md border border-border p-4 space-y-2">
                    <Typography variant="label" variantColor="none" className="mb-2 !text-[#f5bc6f]">{t('product.productLabel')}</Typography>
                    <GridRow cols={2} spacingY={1} spacingX={2}>
                      <GridCol span={1}><DT>{tGlobal('product.category')}</DT></GridCol>
                      <GridCol span={1}><DD loading={isFetching}>{tType(`ProductCategory.${product?.category ?? 'Other'}`)}</DD></GridCol>
                      <GridCol span={1}><DT>{tGlobal('product.unitScheme')}</DT></GridCol>
                      <GridCol span={1}>
                        <DD loading={isFetching} isEmpty={!isDefinedString(product?.category === 'Felleskostnader' ? 'AreaMetric' : product?.unit?.scheme)} emptyValue={tGlobal('product.withoutUnitScheme')}>
                          {tType(`UnitScheme.${product?.category === 'Felleskostnader' ? 'AreaMetric' : product?.unit?.scheme}`)}
                          {!isFetching && product?.category !== 'Felleskostnader' && <Text as="span" className="italic ml-1 text-secondary inline font-normal" isEmpty={!isDefinedString(product?.unit?.label)} emptyValue={''}>({product?.unit?.label})</Text>}
                        </DD>
                      </GridCol>
                      {isErpConfigured && (
                        <>
                          <GridCol span={1}><DT>{tGlobal('product.sourceIdentifier')}</DT></GridCol>
                          <GridCol span={1}><DD loading={isFetching} isEmpty={!isDefinedString(product?.sourceId)}>{product?.sourceId}{tripletexProductName ? ` (${tripletexProductName})` : ''}</DD></GridCol>
                        </>
                      )}
                    </GridRow>
                    <div className="pt-2">
                      <DT className="mb-1">{tGlobal('product.description')}</DT>
                      <DD loading={isFetching} isEmpty={!isDefinedString(product?.description)} className="text-sm" variantFontSize="none">{product?.description}</DD>
                    </div>
                  </div>
                  <div className="rounded-md border border-border p-4 space-y-2">
                    <Typography variant="label" variantColor="none" className="mb-2 !text-[#f5bc6f]">{t('product.pricing')}</Typography>
                    <GridRow cols={2} spacingY={1} spacingX={2}>
                      <GridCol span={1}><DT>{tGlobal('price.unitAmount')}</DT></GridCol>
                      <GridCol span={1}>
                        <DD loading={isFetchingPrices} isEmpty={!isDefinedNumber(firstPrice?.unitAmount?.amount)}>
                          <Text className="font-medium">{toCurrencyStringOrUndefined(firstPrice?.unitAmount?.amount, undefined, { currency: firstPrice?.unitAmount?.currency?.code as CurrencyCode })}</Text>
                          {isValidDefinedPriceInterval(firstPrice) && <Text as="span" className="ml-1 font-normal text-secondary">{toIntervalString(firstPrice?.interval)}</Text>}
                        </DD>
                      </GridCol>
                      <GridCol span={1}><DT>{tGlobal('price.type')}</DT></GridCol>
                      <GridCol span={1}>
                        <DD loading={isFetchingPrices} isEmpty={firstPrice == null}>
                          {firstPrice?.recurring ? tType('PriceType.Recurring') : tType('PriceType.OneTime')}
                        </DD>
                      </GridCol>
                      <GridCol span={1}><DT>{tGlobal('price.description')}</DT></GridCol>
                      <GridCol span={1}><DD loading={isFetchingPrices} isEmpty={!isDefinedString(firstPrice?.description)}>{firstPrice?.description}</DD></GridCol>
                    </GridRow>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-md border border-border p-4 space-y-4">
                    <Typography variant="label" variantColor="none" className="mb-2 !text-[#f5bc6f]">{t('product.productLabel')}</Typography>
                    <FormField<FormValues, FormPath> name="name" label={tGlobal('product.name')} defaultValue={''} required render={({ field }) => <Input {...field} />} />
                    <FormField<FormValues, FormPath> name="category" label={tGlobal('product.category')} defaultValue={'Other'} render={({ field }) => (
                      <Select {...field}>{PRODUCT_CATEGORY_OPTIONS.map((cat) => <option key={cat} value={cat}>{tType(`ProductCategory.${cat}`)}</option>)}</Select>
                    )} />
                    <FormField<FormValues, FormPath> name="unitScheme" label={<span>{tGlobal('product.unitScheme')}<InfoTooltip className="ml-2" message={t('unitSchemeDisabledTooltip')} messageClassName="w-40"><FaInfoCircle className="h-4 w-4 translate-y-0.5" /></InfoTooltip></span>} defaultValue={''} render={({ field }) => (
                      <Select {...field} disabled={isFelleskostnader} onChange={(e) => { handleSelectUnitScheme(e); field.onChange(e); }}>
                        <option value="">{tGlobal('product.withoutUnitScheme')}</option>
                        {UNIT_SCHEME_OPTIONS.map((type) => <option key={type} value={type}>{tType(`UnitScheme.${type}`)}</option>)}
                      </Select>
                    )} />
                    {unitScheme === 'Custom' && <FormField<FormValues, FormPath> name="unitLabel" label={tGlobal('product.unitLabel')} defaultValue={''} render={({ field }) => <Input {...field} />} />}
                    {isErpConfigured && (
                      <FormField<FormValues, FormPath> name="sourceIdentifier" defaultValue={''} label={tGlobal('product.sourceIdentifier')} required render={({ field }) => (
                        <TripletexProductSelect {...field} />
                      )} />
                    )}
                    <FormField<FormValues, FormPath> name="description" label={tGlobal('product.description')} defaultValue={''} render={({ field }) => <Input {...field} />} />
                  </div>
                  <div className="rounded-md border border-border p-4 space-y-4">
                    <Typography variant="label" variantColor="none" className="mb-2 !text-[#f5bc6f]">{t('product.pricing')}</Typography>
                    <FormField name="prices.0.unitAmount" defaultValue={''} label={tGlobal('price.unitAmount')} required render={({ field }) => (
                      <CurrencyAdornment position="start" symbol={unitCurrencySymbol}>
                        <Input type="number" readOnly={hasSubProducts} className={unitCurrencySymbol && (typeof unitCurrencySymbol === 'string' && unitCurrencySymbol.length >= 3 ? '!pl-11' : '!pl-6')} {...field} />
                      </CurrencyAdornment>
                    )} />
                    {hasSubProducts && <Text className="text-xs text-secondary">{t('product.priceFromSubProducts')}</Text>}
                    <FormField name="prices.0.unitCurrencyCode" label={tGlobal('price.unitCurrency')} defaultValue={''} required render={({ field: { value, ref, ...field } }) => (
                      <CurrencyAutocomplete selected={value} defaultCurrencyCode={defaultCurrencyCode} {...field} />
                    )} />
                    <div>
                      <Label className="mb-1">{tGlobal('price.type')}</Label>
                      <div className="flex items-center space-x-2">
                        <Button size="none" className="px-4 py-1 text-sm rounded-lg" color={hasInterval ? 'primary' : 'primary-light'} onClick={() => handleSelectHasInterval(true)}>{tType('PriceType.Recurring')}</Button>
                        <Button size="none" className="px-4 py-1 text-sm rounded-lg" color={!hasInterval ? 'primary' : 'primary-light'} onClick={() => handleSelectHasInterval(false)} disabled={hasSubProducts}>{tType('PriceType.OneTime')}</Button>
                      </div>
                    </div>
                    <FormField name="prices.0.description" label={tGlobal('price.description')} defaultValue={''} render={({ field }) => <Input {...field} />} />
                    {pricesErrorMessage && <p className="text-right text-sm text-red-500">{pricesErrorMessage}</p>}
                  </div>
                </div>
              )}
            </CardContent>
          </form>
        </FormProvider>
      </Card>
      <CancelRequestModal open={cancelingRequest} onConfirm={handleConfirmCancelRequest} onCancel={handleCancelCancelRequest} />
    </>
  );
}
